import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  faEye,
  faEyeSlash,
  faLock,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/buttons/IconButton';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { CircleFlag } from 'react-circle-flags';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link, Route, Switch } from 'react-router-dom';
import { UserContext } from 'stores';
import logoExtended from 'style/imgs/logo_extended.png';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import {
  BecomeCustomerText,
  ErrorMessage,
  FormContent,
  FormContentGroup,
  FormLogoImage,
  FormSection,
  FormSectionContent,
  InputContainer,
  LanguageOption,
  LanguageSelector,
  ResponseText,
  StyledButton,
} from './styles';

function LoginFormSection({
  passwordValue,
  usernameValue,
  onPasswordInput,
  onUsernameInput,
  onSubmit,
  hasLoginError,
  defaultLanguage,
  handleChangeLanguage,
}) {
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const userStore = React.useContext(UserContext);

  const onUsernameChange = (event) => {
    onUsernameInput(event.target.value);
  };

  const onPasswordChange = (event) => {
    onPasswordInput(event.target.value);
  };

  const onPressLogIn = (event) => {
    event.preventDefault();
    onSubmit();
  };

  const toggleIsPasswordShown = (event) => {
    event.preventDefault();

    // Avoid jumpcloud from showing password
    if (event?.nativeEvent?.pointerType === '') return;

    setIsPasswordShown((prevIsPasswordShown) => !prevIsPasswordShown);
  };

  const languages = [
    {
      value: 'en-US',
      label: 'EN',
      countryCode: 'gb',
    },
    {
      value: 'nl-NL',
      label: 'NL',
      countryCode: 'nl',
    },
  ];

  const defaultSelectedLanguage = languages.find(
    (lang) => lang.value === defaultLanguage
  );

  return (
    <FormSection>
      <FormSectionContent>
        <LanguageSelector
          onChange={(e) => handleChangeLanguage(e)}
          defaultValue={defaultSelectedLanguage.label}
        >
          {languages.map(({ countryCode, label, value }) => (
            <LanguageOption value={value}>
              <CircleFlag countryCode={countryCode} height="15" />
              <div style={{ marginLeft: '8px' }}>{label}</div>
            </LanguageOption>
          ))}
        </LanguageSelector>
        <FormLogoImage src={logoExtended} alt="Joboti Logo" />
        <Switch>
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/">
            {!userStore.isLoggedIn && (
              <React.Fragment>
                {/* <FormHeading>
                  <FormattedMessage id="pages/Login/LoginFormSection/WelcomeBack" />
                  <span className="text-kernel-text">
                    {' '}
                    Textkernel Candidate Engagement
                  </span>
                </FormHeading> */}
                <form>
                  <FormContent>
                    <FormContentGroup>
                      <label htmlFor="username">
                        <FormattedMessage id="pages/Login/LoginFormSection/UsernameLabel" />
                      </label>
                      <InputContainer>
                        <FontAwesomeIcon icon={faUser} />
                        <input
                          value={usernameValue}
                          onChange={onUsernameChange}
                          id="username"
                          name="username"
                          autoComplete="username"
                        />
                      </InputContainer>
                    </FormContentGroup>
                    <FormContentGroup>
                      <label htmlFor="password">
                        <FormattedMessage id="pages/Login/LoginFormSection/PasswordLabel" />
                      </label>
                      <InputContainer>
                        <FontAwesomeIcon icon={faLock} />
                        <input
                          value={passwordValue}
                          onChange={onPasswordChange}
                          onKeyDown={(event) => {
                            if (event.keyCode === 13) {
                              onPressLogIn(event);
                            }
                          }}
                          type={isPasswordShown ? 'input' : 'password'}
                          id="password"
                          name="password"
                          autoComplete="current-password"
                        />
                        <IconButton
                          onClick={toggleIsPasswordShown}
                          icon={
                            <FontAwesomeIcon
                              icon={isPasswordShown ? faEyeSlash : faEye}
                            />
                          }
                        />
                      </InputContainer>
                      {hasLoginError && (
                        <ErrorMessage>
                          <FontAwesomeIcon icon={faTimesCircle} />
                          <ResponseText>
                            <FormattedMessage id="pages/Login/LoginFormSection/IncorrectCredential" />
                          </ResponseText>
                        </ErrorMessage>
                      )}
                      <Link to="/forgot-password">
                        <a>
                          <FormattedMessage id="pages/Login/LoginFormSection/ForgetPassword" />
                        </a>
                      </Link>
                    </FormContentGroup>

                    <StyledButton onClick={onPressLogIn}>
                      <FormattedMessage id="pages/Login/LoginFormSection/LogIn" />
                    </StyledButton>
                  </FormContent>
                </form>
                <BecomeCustomerText>
                  <FormattedMessage id="pages/Login/LoginFormSection/NotCustomerYet" />{' '}
                  <a href="https://joboti.com" target="_blank" rel="noreferrer">
                    <FormattedMessage id="pages/Login/LoginFormSection/BecomeCustomer" />
                  </a>
                </BecomeCustomerText>
              </React.Fragment>
            )}
            {userStore.isLoggedIn && userStore.requiresPasswordReset && (
              <ResetPassword firstTimeLogin />
            )}
          </Route>
        </Switch>
      </FormSectionContent>
    </FormSection>
  );
}

LoginFormSection.propTypes = {
  passwordValue: PropTypes.string.isRequired,
  usernameValue: PropTypes.string.isRequired,
  onPasswordInput: PropTypes.func.isRequired,
  onUsernameInput: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default injectIntl(observer(LoginFormSection));
